import { Spinner, Text, XStack, YStack } from "tamagui";

import PointsRedeemRow from "@components/points/PointsRedeemRow";
import PointsSavingBanner from "@components/points/PointsSavingBanner";
import { useCartPointsRedeem } from "@screen/Home/pages/GasShop/utils/useCartPointsRedeem";
import { useOrder } from "@utils/store/cart/useOrder";
import { kiosk } from "../kioskTheme";
import { Card, SectionLabel } from "./ConfirmParts";

// Kiosk 的 Card 每一列都是 icon + 標籤 + 值，列間有分隔線 —— 點數跟著同一種身段。
const SKIN = { ink: kiosk.ink, sub: kiosk.grey, line: "#f0f2f6", accent: kiosk.blue, icon: true };

/**
 * Kiosk 結算頁的「點數折抵」。折抵計算與瓦斯訂購共用 useCartPointsRedeem，
 * 兩條下單流程折出來的金額必然一致；外觀則用 Kiosk 自己的 SectionLabel + Card，
 * 與這頁其他每一段（配送資訊、商品、付款與發票、優惠券）長成同一種卡。
 *
 * 帳戶完全沒點數時整區不出現。專員報價／隱藏金額的單子改走 intentOnly：
 * 金額要接單後才有，折抵算不出數字，所以只收「要不要折」。
 */
const PointsRedeemCard = ({ intentOnly = false }: { intentOnly?: boolean }) => {
  const { pointsRedeem, setPointsRedeem, pointsIntent, setPointsIntent } = useOrder();
  const { result, balance, loading, ready, empty } = useCartPointsRedeem();

  if (empty) return null;

  return (
    <>
      <SectionLabel>點數折抵</SectionLabel>
      <Card>
        {loading ? (
          <XStack ai="center" gap={8} py={14}>
            <Spinner size="small" color={kiosk.blue} />
            <Text fontSize={13} color={kiosk.grey}>
              讀取點數…
            </Text>
          </XStack>
        ) : (
          <YStack>
            {balance.storedGas > 0 && (
              <PointsRedeemRow
                kind="stored_gas"
                balance={balance.storedGas}
                used={result.storedGas}
                max={result.maxStoredGas}
                ready={ready}
                onChange={(storedGas) => setPointsRedeem({ ...pointsRedeem, storedGas })}
                skin={SKIN}
                isLast={balance.account <= 0}
                intent={
                  intentOnly
                    ? { on: pointsIntent.storedGas, onToggle: (on) => setPointsIntent({ ...pointsIntent, storedGas: on }) }
                    : undefined
                }
              />
            )}
            {balance.account > 0 && (
              <PointsRedeemRow
                kind="account"
                balance={balance.account}
                used={result.account}
                max={result.maxAccount}
                ready={ready}
                onChange={(account) => setPointsRedeem({ ...pointsRedeem, account })}
                skin={SKIN}
                isLast
                intent={
                  intentOnly
                    ? { on: pointsIntent.account, onToggle: (on) => setPointsIntent({ ...pointsIntent, account: on }) }
                    : undefined
                }
              />
            )}
          </YStack>
        )}
      </Card>
      {/* 省了多少當場說一次：摘要裡也有，但那要再捲一段才看得到。
          放在卡外是為了讓它整條撐開 —— 這條訊息值得自己一塊地方。 */}
      {!intentOnly && !loading && result.discount > 0 && (
        <YStack mt={9}>
          <PointsSavingBanner discount={result.discount} />
        </YStack>
      )}
    </>
  );
};

export default PointsRedeemCard;
